/**
 * Pure TEA update for light/dark theme preference.
 *
 * Shell: `useTheme.ts` (DOM class + localPrefs Cmds via `dispatchInto`).
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";

export type Theme = "light" | "dark";

export type ThemeModel = {
  readonly theme: Theme;
};

export function initialThemeModel(theme: Theme): ThemeModel {
  return { theme };
}

export type ThemeMsg =
  { readonly type: "Set"; readonly theme: Theme } | { readonly type: "Toggle" };

export type ThemeCmd = {
  readonly kind: "applyTheme";
  readonly theme: Theme;
};

export function updateTheme(
  model: ThemeModel,
  msg: ThemeMsg,
): readonly [ThemeModel, readonly ThemeCmd[]] {
  switch (msg.type) {
    case "Set":
      if (msg.theme === model.theme) return [model, []];
      return [{ theme: msg.theme }, [{ kind: "applyTheme", theme: msg.theme }]];
    case "Toggle": {
      const theme: Theme = model.theme === "dark" ? "light" : "dark";
      return [{ theme }, [{ kind: "applyTheme", theme }]];
    }
    default:
      return assertNever(msg);
  }
}

export const updateThemeFn: UpdateFn<ThemeModel, ThemeMsg, ThemeCmd> =
  updateTheme;
